import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  EnhancedDimension,
  Icon,
  Table,
  Tag,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useShiftTimeFormatted } from 'modules/shift_management/hooks/useShiftTimeFormatted'
import {
  trackDeleteDefaultShiftStart,
  trackDeleteDefaultShiftSubmit,
  trackEditDefaultShiftStart,
} from 'modules/shift_management/lib/trackEvents'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import type DefaultShift from 'modules/shift_management/store/internal/models/DefaultShift'
import BreakInformation from 'modules/time_planning/components/Breaks/BreakInformation'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import SavedShiftOptions from './SavedShiftOptions'
import { Sticky } from './utils'

interface Props {
  defaultShift: DefaultShift
  plannedBreaks?: PlannedBreak[]
  hoursMinWidth: EnhancedDimension
}

const SavedShift = ({ defaultShift, plannedBreaks, hoursMinWidth }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const sticky: Sticky = {
    position: 'left',
  }
  const formatShiftTime = useShiftTimeFormatted()

  const {
    color,
    title,
    startAt,
    endAt,
    overnightShift,
    location,
    workArea,
    notes,
    allCompany,
    teamsIdsVisibilities,
    locationsIdsVisibilities,
    combinedAccessNames,
  } = defaultShift

  const [isOpen, toggleIsOpen] = useToggle(false)

  // The same flag is used to indicate extras like location, work area and notes
  const hasColors = hasFeature('shift_management_colours_for_shifts')
  const hasBreaks = hasFeature('time_settings_breaks')

  const onClickEdit = () => {
    trackEditDefaultShiftStart({
      name: title,
      color,
      startAt,
      endAt,
      workplace: location?.id,
      workarea: workArea?.id,
      comments: notes,
      allCompany: allCompany,
      teamsIdsVisibilities: teamsIdsVisibilities,
      locationsIdsVisibilities: locationsIdsVisibilities,
      daysPreselected: false,
    })
    navigate(navigation.shifts.savedShifts.editSavedShift.fullPath({ id: defaultShift.id }))
  }

  const onClickDelete = () => {
    trackDeleteDefaultShiftStart({
      name: title,
      color,
      startAt,
      endAt,
      workplace: location?.id,
      workarea: workArea?.id,
      comments: notes,
      allCompany: allCompany,
      teamsIdsVisibilities: teamsIdsVisibilities,
      locationsIdsVisibilities: locationsIdsVisibilities,
    })
    toggleIsOpen(true)()
  }

  const onSubmitDelete = async () => {
    trackDeleteDefaultShiftSubmit({
      name: title,
      color,
      startAt,
      endAt,
      workplace: location?.id,
      workarea: workArea?.id,
      comments: notes,
      allCompany: allCompany,
      teamsIdsVisibilities: teamsIdsVisibilities,
      locationsIdsVisibilities: locationsIdsVisibilities,
    })
    const result = await asyncAction(() => defaultShift.toPayload().delete(), {
      successMessage: i18n.t('shift_management.saved_shifts.actions.delete.success'),
    })

    if (isSuccessful(result)) {
      defaultShiftsCollection.remove(defaultShift.id)
      toggleIsOpen(false)
    }
  }

  return (
    <>
      <Table.Row>
        <Table.Cell sticky={sticky}>
          <Box flexDirection="row" alignItems="center" gap="s4" minWidth="s200">
            <Icon.Small icon="circle" color={color || 'grey400'} />
            <Box>
              <Text size="300" weight="medium" color="grey1000" ellipsis>
                {title}
              </Text>
            </Box>
          </Box>
        </Table.Cell>
        <Table.Cell>
          <Box flexDirection="row" gap="s8" alignItems="center" minWidth={hoursMinWidth}>
            <Tag type={Tag.Types.grey} ellipsis={false}>
              {formatShiftTime(startAt, endAt)}
            </Tag>
            {overnightShift && (
              <Tag type={Tag.Types.grey}>{i18n.t('shift_management.plus_1_day_tag')}</Tag>
            )}
          </Box>
        </Table.Cell>
        {hasBreaks && (
          <Table.Cell>
            <Box flexDirection="column" gap="s8" minWidth="s140">
              {plannedBreaks?.map((plannedBreak) => (
                <Box key={plannedBreak.id} flexDirection="row" alignItems="center">
                  <BreakInformation plannedBreak={plannedBreak} />
                </Box>
              ))}
            </Box>
          </Table.Cell>
        )}
        <Table.Cell>
          <Box flexDirection="row" alignItems="center" minWidth="s140">
            {hasColors && (
              <Text size="300" weight="medium" color="grey1000" ellipsis>
                {location?.name}
              </Text>
            )}
          </Box>
        </Table.Cell>
        <Table.Cell>
          <Box flexDirection="row" alignItems="center" minWidth="s140">
            {hasColors && (
              <Text size="300" weight="medium" color="grey1000" ellipsis>
                {workArea?.name}
              </Text>
            )}
          </Box>
        </Table.Cell>
        <Table.Cell>
          <Box flexDirection="column" gap="s8" minWidth="s140">
            {allCompany ? (
              <Box flexDirection="row" alignItems="center">
                <Tag type={Tag.Types.malibu}>
                  {i18n.t('shift_management.saved_shifts.form_access.all_company')}
                </Tag>
              </Box>
            ) : (
              combinedAccessNames.map((name) => (
                <Box key={name} flexDirection="row" alignItems="center">
                  <Tag type={Tag.Types.malibu}>{name}</Tag>
                </Box>
              ))
            )}
          </Box>
        </Table.Cell>

        <SavedShiftOptions onClickEdit={onClickEdit} onClickDelete={onClickDelete} />
      </Table.Row>

      {isOpen && (
        <Modal
          illustration="alert"
          title={i18n.t('shift_management.saved_shifts.actions.delete.title')}
          onClose={toggleIsOpen(false)}
          footer={
            <>
              <Button onClick={toggleIsOpen(false)} type="secondary">
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={onSubmitDelete} type="primary">
                {i18n.t('shift_management.saved_shifts.actions.delete.confirm')}
              </Button>
            </>
          }
        >
          <Text.Normal>{i18n.t('shift_management.saved_shifts.actions.delete.prompt')}</Text.Normal>
        </Modal>
      )}
    </>
  )
}

export default observer(SavedShift)
